/**
 * src/features/auth/ui/RequestPasswordResetForm.tsx
 *
 * Two-step ("form" -> "sent") password-reset request form, rendered by LoginCard as the
 * third email sub-method ('request-reset'). Structurally mirrors MagicLinkForm (Phase 42):
 * same AnimatePresence mode="wait" shape, same emailRef focus-on-mount, same WR-01
 * { data, error } never-throws contract.
 *
 * This file is the app's ONLY write site for sessionStorage['estimatix:pending_reset_email']
 * (PENDING_RESET_EMAIL_KEY below) — the same single-write-site convention
 * lastLoginMethod.ts established for LAST_LOGIN_METHOD_KEY. ResetPasswordPage.tsx reads and
 * removes it; nothing else touches it.
 *
 * T-43-02 (account enumeration): the server already answers identically for a known and an
 * unknown email address (better-auth's requestPasswordReset always returns
 * `{ status: true }`). This component advances to the "sent" step for EVERY outcome — it
 * never inspects `result.error` to decide whether to show the neutral confirmation, because
 * there is nothing in the response worth branching on, and branching would reopen the
 * enumeration the server already closed.
 */
import { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail } from 'lucide-react';
import Button from '@/shared/ui/button';
import { authClient } from '@/lib/auth-client';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';

/** Sole write site in the app (grep-provable) — read back by ResetPasswordPage.tsx, which
 * removes it immediately on read (one-shot). Colon convention, matching
 * LAST_LOGIN_METHOD_KEY (lastLoginMethod.ts), not PostExportPrompt's hyphen convention. */
export const PENDING_RESET_EMAIL_KEY = 'estimatix:pending_reset_email';

export interface RequestPasswordResetFormProps {
  /** Returns the email sub-method to 'password' — this form does not remember where the
   * user came from; requesting a reset always implies they want to sign in with a password
   * next (43-UI-SPEC.md Surface 0). */
  onBack: () => void;
}

export const RequestPasswordResetForm: FC<RequestPasswordResetFormProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const shortDuration = reduced ? 0 : 0.2;

  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'form' | 'sent'>('form');
  const [isLoading, setIsLoading] = useState(false);

  // WR-01 (43-REVIEW.md): synchronous in-flight guard — same idiom and rationale as the
  // three sibling forms (PasswordSignInForm.tsx:39-43, SecuritySettingsCard.tsx,
  // ResetPasswordPage.tsx). React's state updates are batched, so two `fireEvent.submit`
  // calls (or two fast real clicks) in the same tick would both see `isLoading === false` if
  // state alone were the guard — a ref mutates immediately, so the second call in the same
  // tick correctly observes the first already in flight. Without this, a double-submit burns
  // the endpoint's own rate-limit budget (window:60/max:3) on an accidental double-click.
  const inFlightRef = useRef(false);

  // Focus email on mount — same convention as MagicLinkForm's emailRef.
  const emailRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlightRef.current) return;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    inFlightRef.current = true;
    setIsLoading(true);

    // Write BEFORE sending — a storage failure (private browsing, disabled site data) must
    // degrade only the later best-effort email pre-fill on /auth/reset, never block the
    // actual reset email from being requested.
    try {
      sessionStorage.setItem(PENDING_RESET_EMAIL_KEY, trimmedEmail);
    } catch {
      // Swallowed intentionally — see comment above.
    }

    // WR-01: better-auth returns { data, error }, never throws. The result is deliberately
    // not inspected — see the file header comment on why every outcome advances to "sent".
    await authClient.requestPasswordReset({ email: trimmedEmail, redirectTo: '/auth/reset' });
    inFlightRef.current = false;
    setStep('sent');
    setIsLoading(false);
  };

  return (
    <AnimatePresence mode="wait">
      {step === 'form' ? (
        <motion.div
          key="form"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: shortDuration }}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-subhead font-bold text-slate-900 dark:text-slate-100">
                {t('auth.request_reset.title', 'Reset your password')}
              </span>
              <span className="text-body text-slate-500 dark:text-slate-400">
                {t('auth.request_reset.body', "Enter your email and we'll send you a reset link.")}
              </span>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-body font-medium">{t('auth.email.label', 'Email')}</span>
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.email.placeholder', 'you@example.com')}
                required
                autoComplete="username"
                disabled={isLoading}
                className="h-11 border border-slate-200 dark:border-slate-700 rounded-input px-3 text-body focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 disabled:opacity-50"
              />
            </label>
            <Button
              variant="default"
              size="lg"
              type="submit"
              className="w-full"
              disabled={isLoading || !email.trim()}
              aria-busy={isLoading}
            >
              {isLoading ? t('auth.submit_loading', 'Sending...') : t('auth.request_reset.cta', 'Send reset link')}
            </Button>
            <Button variant="link" type="button" onClick={onBack} className="self-center">
              {t('auth.request_reset.back', 'Back to sign in')}
            </Button>
          </form>
        </motion.div>
      ) : (
        <motion.div
          key="sent"
          className="flex flex-col gap-3 text-center py-2"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: shortDuration }}
        >
          <Mail size={32} className="mx-auto text-blue-600 dark:text-blue-400" aria-hidden="true" />
          <p className="text-subhead font-bold text-slate-900 dark:text-slate-100">
            {t('auth.request_reset.sent_title', 'Check your email')}
          </p>
          <p className="text-body text-slate-500 dark:text-slate-400">
            {t(
              'auth.request_reset.sent_body',
              'If that email is registered, a reset link is on its way. The link is valid for 1 hour.',
            )}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
